import * as recallClient from '@otter-ai/recall-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { USER_ROLES } from '#constants';
import { userProfileQueryCreator } from '#query';

type Args = {
  department: string;
  role: string;
};

export type SetCurrentUserDepartmentAndRoleResult = {
  setDepartmentAndRole: (args: Args) => Promise<void>;
  isPending: boolean;
};

/**
 * Update the current user's department and role.
 * @example
 * ```tsx
 * function Example({ user }) {
 *   const [department, setDepartment] = useState(user.department);
 *   const [role, setRole] = useState(user.role);
 *   const setCurrentUserDepartmentAndRole = useSetCurrentUserDepartmentAndRole();
 *   const updateUserDepartmentAndRole = () => {
 *    // Validate input before calling setCurrentUserDepartmentAndRole
 *    setCurrentUserDepartmentAndRole({ department, role });
 *   };
 *   return (<>
 *     <select value={department} onChange={((e) => setDepartment(e.target.value))}>...</select>
 *     <select value={role} onChange={((e) => setRole(e.target.value))}>...</select>
 *     <button onClick={updateUserDepartmentAndRole}>Save</button>
 *   </>);
 * }
 * ```
 */
export function useSetCurrentUserDepartmentAndRole(): SetCurrentUserDepartmentAndRoleResult {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (args: Args) => {
      // --- Final validation ---
      // Validate the department and role before calling setCurrentUserDepartmentAndRole().
      if (validateDepartmentAndRole(args).length > 0) {
        throw new SetCurrentUserDepartmentAndRoleInvalidInputError();
      }

      // --- Prepare data ---
      const { department, role } = args;

      // --- Call API ---
      await recallClient.editProfileRoleDepartment({
        body: {
          department: department as string,
          role: role as string,
          // TODO(OTWEB-8649): Remove `use_case` and `use_case_other` when they are optional in api-spec
          use_case: '',
          use_case_other: '',
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userProfileQueryCreator().queryKey,
        exact: true,
        refetchType: 'all',
      });
    },
  });

  return { setDepartmentAndRole: mutateAsync, isPending };
}

export function validateDepartmentAndRole(
  args: Args,
): ReadonlyArray<{ name: string; reason: string }> {
  const errors: Array<{ name: string; reason: string }> = [];

  if (!args.department) {
    errors.push({
      name: 'department',
      reason: 'required',
    });
  }

  if (!args.role) {
    errors.push({
      name: 'role',
      reason: 'required',
    });
  } else if (!(USER_ROLES as ReadonlyArray<string>).includes(args.role)) {
    errors.push({
      name: 'role',
      reason: 'invalid',
    });
  }

  return errors;
}

class SetCurrentUserDepartmentAndRoleInvalidInputError extends Error {
  name = 'SetCurrentUserDepartmentAndRoleInvalidInputError';
  message = 'The department and role inputs are invalid';
}
